import React, { useEffect, useState } from 'react';
import { ChevronLeft, Share2, Phone, MessageCircle, FileText, Check, Circle, Upload, Download } from 'lucide-react';
import {
  getUserLoanTimeline,
  getTreatmentInvoiceData,
  getFinalLenderDecisionDetails,
  getDisburseDataByLoanId,
} from '../../api/loan-action-creator';
import { formatAmount, isInvoiceReceived } from '../../utils/format';
import DisbursalOrderOverlay from './transactionDetails/DisbursalOrderOverlay';
import InvoiceUploadModal from './InvoiceUploadModal';

const formatTimelineDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const getInitials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const getPhoneDigits = (transaction) =>
  String(transaction.mobileNumber || transaction.patientPhoneNo || '').replace(/\D/g, '');

const hasInvoicePayload = (data) => {
  if (!data || typeof data === 'string') return false;
  if (Array.isArray(data)) return data.length > 0;
  return Boolean(data.invoiceNumber || data.fileUrl || data.invoiceUrl || data.billingDate);
};

const isLoanOpsCompleteText = (value) =>
  /loan operations? complete/i.test(String(value || ''));

const isLoanOpsComplete = (item) =>
  isLoanOpsCompleteText(item?.status || item?.activity || item?.userStatus);

const pickAssignedLender = (data) => {
  const list = Array.isArray(data) ? data : data ? [data] : [];
  if (list.length === 0) return null;
  return list.find((item) => item?.currentlyMapped) || list[0];
};

const getStatusText = (transaction, statusLabel) =>
  String(statusLabel || transaction?.status || '').toLowerCase();

const isRejectedStatus = (text) => text.includes('reject');

const isShareLinkDisabledStatus = (text) =>
  text.includes('reject') ||
  text.includes('disburs') ||
  text.includes('utr');

const hasPositiveLimit = (value) => {
  if (value == null || value === '') return false;
  const numeric = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(numeric) && numeric > 0;
};

const TransactionDrawer = ({
  transaction,
  statusLabel,
  statusTone,
  checklistItems = [],
  onClose,
  onInvoiceUploaded,
}) => {
  const [timeline, setTimeline] = useState([]);
  const [invoiceReceived, setInvoiceReceived] = useState(
    isInvoiceReceived(transaction?.treatmentInvoice) || Boolean(transaction?.postApprovalChecklist?.treatmentBill)
  );
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showDisbursalOrder, setShowDisbursalOrder] = useState(false);
  const [lenderLimit, setLenderLimit] = useState(transaction?.maxLimit || '');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (!transaction?.loanId) return;

    getUserLoanTimeline(
      transaction.loanId,
      transaction.userId,
      (data) => setTimeline(Array.isArray(data) ? data : []),
      () => setTimeline([])
    );

    getTreatmentInvoiceData(
      transaction.loanId,
      (res) => {
        setInvoiceReceived(
          hasInvoicePayload(res?.data) || isInvoiceReceived(transaction.treatmentInvoice)
        );
      },
      () => setInvoiceReceived(isInvoiceReceived(transaction.treatmentInvoice))
    );
  }, [transaction]);

  const hasApprovedStatus =
    (String(transaction?.status || '').toLowerCase().includes('approved') &&
      !String(transaction?.status || '').toLowerCase().includes('reject')) ||
    timeline.some((item) => String(item?.status || item?.activity || '').toLowerCase().includes('approved'));

  useEffect(() => {
    if (!transaction?.loanId) return;

    getFinalLenderDecisionDetails(
      transaction.loanId,
      (res) => {
        const assigned = pickAssignedLender(res?.data);
        setLenderLimit(
          assigned?.limit != null && assigned.limit !== ''
            ? assigned.limit
            : transaction.maxLimit || ''
        );
      },
      () => {
        setLenderLimit(transaction.maxLimit || '');
      }
    );
  }, [transaction]);

  const canViewDisbursalOrder =
    timeline.some(isLoanOpsComplete) || isLoanOpsCompleteText(transaction?.status);

  useEffect(() => {
    if (!transaction?.loanId || !canViewDisbursalOrder) return;
    getDisburseDataByLoanId(transaction.loanId, () => {}, () => {});
  }, [transaction?.loanId, canViewDisbursalOrder]);

  const handleShare = () => {
    const link = transaction.shareableLink || transaction.onboardingUrl;
    if (!link) {
      alert('No application link available yet.');
      return;
    }
    navigator.clipboard.writeText(link);
    alert('Application link copied to clipboard.');
  };

  const refreshInvoiceState = () => {
    if (!transaction?.loanId) return;
    getTreatmentInvoiceData(
      transaction.loanId,
      (res) => {
        setInvoiceReceived(
          hasInvoicePayload(res?.data) || isInvoiceReceived(transaction.treatmentInvoice)
        );
      },
      () => setInvoiceReceived(isInvoiceReceived(transaction.treatmentInvoice))
    );
    if (onInvoiceUploaded) onInvoiceUploaded();
  };

  const utrNumber = transaction.utr || transaction.utrNo || '';
  const phone = getPhoneDigits(transaction);
  const waNumber = phone.length === 10 ? `91${phone}` : phone;
  const statusText = getStatusText(transaction, statusLabel);
  const isRejected = isRejectedStatus(statusText);
  const canShareApplicationLink = !isShareLinkDisabledStatus(statusText);
  const showLenderLimit = hasApprovedStatus && hasPositiveLimit(lenderLimit);
  const checklist = {
    ...(transaction.postApprovalChecklist || {}),
    treatmentBill: invoiceReceived,
  };
  const doneCount = checklistItems.filter((item) => Boolean(checklist[item.key])).length;
  const timelineItems = timeline.length
    ? timeline.slice(0, 6)
    : [{ status: transaction.status, addedOn: transaction.appliedAt }];

  return (
    <div className="lt-drawer-root">
      <button type="button" className="lt-drawer-overlay" aria-label="Close details" onClick={onClose} />
      <aside className="lt-drawer" role="dialog" aria-labelledby="lt-drawer-title">
        <header className="lt-drawer-top">
          <button type="button" className="lt-drawer-close" onClick={onClose} aria-label="Close">
            <ChevronLeft size={22} />
          </button>
          <p>Transaction details</p>
        </header>

        <div className="lt-drawer-body">
          <div className="lt-drawer-identity">
            <span className="lt-avatar lt-avatar-lg">{getInitials(transaction.name)}</span>
            <div className="lt-drawer-identity-copy">
              <div className="lt-drawer-identity-row">
                <h2 id="lt-drawer-title">{transaction.name || 'Applicant'}</h2>
                {statusLabel && <span className={`lt-pill ${statusTone || 'progress'}`}>{statusLabel}</span>}
              </div>
              <p>Application {transaction.applicationId || transaction.loanId || '—'}</p>
            </div>
          </div>

          <div className="lt-drawer-summary">
            <div>
              <span>Loan amount</span>
              <strong>{formatAmount(transaction.loanAmountRaw ?? transaction.amount)}</strong>
            </div>
            <div>
              <span>Clinic</span>
              <strong>{transaction.clinicName || '—'}</strong>
            </div>
            <div>
              <span>Treatment</span>
              <strong>{transaction.treatment || '—'}</strong>
            </div>
            <div>
              <span>Applied</span>
              <strong>{transaction.appliedAt || '—'}</strong>
            </div>
            {utrNumber && (
              <div className="lt-drawer-summary-wide">
                <span>UTR number</span>
                <strong>{utrNumber}</strong>
              </div>
            )}
            {showLenderLimit && (
              <div>
                <span>Lender limit</span>
                <strong>{formatAmount(lenderLimit)}</strong>
              </div>
            )}
          </div>

          <div className="lt-drawer-actions">
            {phone ? (
              <a href={`tel:${phone}`}>
                <Phone size={15} />
                Call
              </a>
            ) : (
              <button type="button" disabled>
                <Phone size={15} />
                Call
              </button>
            )}
            {waNumber ? (
              <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer">
                <MessageCircle size={15} />
                WhatsApp
              </a>
            ) : (
              <button type="button" disabled>
                <MessageCircle size={15} />
                WhatsApp
              </button>
            )}
            <button
              type="button"
              className="lt-share-link-btn"
              disabled={!canShareApplicationLink}
              onClick={handleShare}
            >
              <Share2 size={15} />
              Share application link
            </button>
          </div>

          {!isRejected && (
            <section className="lt-drawer-section">
              <div className="lt-section-head">
                <h3>Post-approval checklist</h3>
                <span>
                  {doneCount}/{checklistItems.length} complete
                </span>
              </div>
              <ul className="lt-check-grid">
                {checklistItems.map((item) => {
                  const done = Boolean(checklist[item.key]);
                  return (
                    <li key={item.key} className={done ? 'done' : ''}>
                      {done ? <Check size={14} /> : <Circle size={14} />}
                      {item.label}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          <section className="lt-drawer-section">
            <h3>Documents</h3>
            <ul className="lt-docs">
              <li>
                <FileText size={16} />
                <strong>Treatment invoice</strong>
                {invoiceReceived ? (
                  <span className="lt-doc-actions">
                    <span className="received">Received</span>
                    <button type="button" className="lt-doc-upload" onClick={() => setShowInvoiceModal(true)}>
                      View
                    </button>
                  </span>
                ) : (
                  <span className="lt-doc-actions">
                    <span className="pending">Pending</span>
                    <button type="button" className="lt-doc-upload" onClick={() => setShowInvoiceModal(true)}>
                      <Upload size={13} />
                      Upload invoice
                    </button>
                  </span>
                )}
              </li>
              <li>
                <FileText size={16} />
                <strong>Disbursal order</strong>
                {canViewDisbursalOrder ? (
                  <span className="lt-doc-actions">
                    <span className="received">Available</span>
                    {/* <button type="button" className="lt-doc-upload" onClick={() => setShowDisbursalOrder(true)}>
                      <Eye size={13} />
                      View
                    </button> */}
                    <button type="button" className="lt-doc-upload" onClick={() => setShowDisbursalOrder(true)}>
                      <Download size={13} />
                      Download
                    </button>
                  </span>
                ) : (
                  <span className="pending">Pending</span>
                )}
              </li>
            </ul>
          </section>

          <section className="lt-drawer-section">
            <h3>Timeline</h3>
            <ol className="lt-timeline">
              {timelineItems.map((item, index) => (
                <li key={`${item.status}-${index}`} className={index === 0 ? 'current' : ''}>
                  <strong>{item.status || item.activity || 'Status updated'}</strong>
                  <span>{formatTimelineDate(item.addedOn)}</span>
                </li>
              ))}
            </ol>
          </section>
        </div>

        <footer className="lt-drawer-footer">
          <p>Need help? Contact your dedicated CarePay executive.</p>
          <a href="https://carepay.money" target="_blank" rel="noopener noreferrer">
            Your CarePay account manager
          </a>
        </footer>
      </aside>
      {showDisbursalOrder && (
        <DisbursalOrderOverlay loanId={transaction.loanId} onClose={() => setShowDisbursalOrder(false)} />
      )}
      <InvoiceUploadModal
        open={showInvoiceModal}
        loanId={transaction.loanId}
        onClose={() => setShowInvoiceModal(false)}
        onSaved={refreshInvoiceState}
      />
    </div>
  );
};

export default TransactionDrawer;
